
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SearchIcon, ChevronRight, HelpCircle } from "lucide-react";
import { useState } from "react";

const Faqs = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const faqCategories = [
    {
      category: "General SEO",
      faqs: [
        {
          question: "What is SEO and why is it important?",
          answer: "SEO (Search Engine Optimization) is the practice of optimizing your website to increase its visibility in search engines like Google. It's important because higher visibility in search results leads to more organic traffic, which can translate to more leads, customers, and revenue for your business."
        },
        {
          question: "How long does it take to see results from SEO?",
          answer: "SEO is a long-term strategy. While some improvements may be visible within 1-2 months, significant results typically take 3-6 months depending on your industry, competition, and website history. We provide regular updates on progress metrics throughout the process."
        },
        {
          question: "What's the difference between on-page and off-page SEO?",
          answer: "On-page SEO refers to optimizations made directly on your website, including content, meta tags, headings, URL structure, and site speed. Off-page SEO involves activities conducted outside your website that affect your rankings, primarily building high-quality backlinks from other websites."
        }
      ]
    },
    {
      category: "Our Services",
      faqs: [
        {
          question: "What's included in the initial SEO audit?",
          answer: "Our comprehensive initial audit includes technical website analysis, content evaluation, competitor research, backlink profile assessment, and keyword opportunity identification. We'll deliver a detailed report with actionable recommendations."
        },
        {
          question: "Do you offer month-to-month contracts or require long-term commitments?",
          answer: "We offer flexible engagement options. While we recommend a minimum 6-month commitment for SEO campaigns to produce meaningful results, we do offer month-to-month arrangements with a slightly higher monthly rate to accommodate your business needs."
        },
        {
          question: "What makes Los Angeles SEO Group different from other agencies?",
          answer: "We combine data-driven strategies with creative content approaches, maintain complete transparency in our work, employ specialized teams for different industry verticals, and customize every campaign based on your specific business goals rather than using a one-size-fits-all approach."
        }
      ]
    },
    {
      category: "Local SEO",
      faqs: [
        {
          question: "How important is local SEO for my business?",
          answer: "If you serve customers in specific geographic areas, local SEO is crucial. It helps your business appear in local search results, Google Maps, and Google Business Profile, making it easier for nearby customers to find you. For businesses with physical locations or service areas, local SEO is often the most important type of SEO."
        },
        {
          question: "How do you approach local SEO for multiple locations?",
          answer: "For multi-location businesses, we create location-specific landing pages, optimize each Google Business Profile, build local citations consistent across all locations, develop location-specific content, and implement proper structured data for each location. Our strategy ensures each location ranks well in its specific geographic area."
        },
        {
          question: "What local directories should my business be listed in?",
          answer: "Beyond Google Business Profile, important directories include Yelp, Yellow Pages, Bing Places, Apple Maps, Better Business Bureau, industry-specific directories, and local chamber of commerce websites. We ensure your business information is consistent across all platforms to boost local search signals."
        }
      ]
    },
    {
      category: "Technical Aspects",
      faqs: [
        {
          question: "How do you measure and report on SEO performance?",
          answer: "We track key performance indicators including organic traffic, keyword rankings, conversion rates, backlink quality/quantity, and engagement metrics. You'll receive detailed monthly reports and have access to a real-time dashboard showing all relevant metrics."
        },
        {
          question: "How important is website speed for SEO?",
          answer: "Website speed is extremely important for SEO. It's a direct ranking factor for Google, and it significantly impacts user experience. Slow-loading sites see higher bounce rates and lower conversion rates. We conduct performance audits and implement optimizations to ensure your website loads quickly on all devices."
        },
        {
          question: "Is mobile optimization important for SEO?",
          answer: "Mobile optimization is critical for SEO. Google uses mobile-first indexing, meaning it primarily uses the mobile version of content for indexing and ranking. We ensure your website is fully responsive, loads quickly on mobile devices, and provides an excellent user experience regardless of screen size."
        }
      ]
    },
    {
      category: "Results & ROI",
      faqs: [
        {
          question: "How do you track conversions from organic traffic?",
          answer: "We implement comprehensive tracking through Google Analytics and other tools to monitor various conversion points, including form submissions, phone calls, chat interactions, and e-commerce transactions. We can also set up advanced attribution modeling to understand the full customer journey from organic search."
        },
        {
          question: "What kind of ROI can I expect from SEO?",
          answer: "ROI varies by industry, competition, and your starting point, but SEO typically provides one of the highest long-term returns of any digital marketing channel. Unlike paid advertising that stops generating leads when you stop paying, SEO continues working for you. Our clients see an average ROI of 1,800% over a 12-month period."
        },
        {
          question: "How often will I receive reports on my SEO campaign?",
          answer: "We provide comprehensive monthly reports detailing all key metrics, progress on tasks, and recommendations. You'll also have access to a real-time dashboard for checking performance anytime. Additionally, we schedule regular strategy calls to review results, answer questions, and discuss next steps."
        }
      ]
    }
  ];

  // Filter FAQs based on search term
  const filteredFaqs = searchTerm ? 
    faqCategories.map(category => ({
      ...category,
      faqs: category.faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(category => category.faqs.length > 0) 
    : faqCategories;

  return (
    <PageTransition>
      <Helmet>
        <title>Frequently Asked Questions | Los Angeles SEO Group</title>
        <meta name="description" content="Find answers to common questions about SEO, our services, pricing, and more. Los Angeles SEO Group provides expert guidance for all your SEO needs." />
        <link rel="canonical" href="https://losangelesseogroup.com/faqs" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqCategories.flatMap(c => c.faqs).map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <Navbar />

      <main className="flex-grow pt-28 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-background to-muted/30 py-16">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center">
              <motion.h1 
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-4"
              >
                Frequently Asked <span className="text-primary">Questions</span>
              </motion.h1>
              <motion.p 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-lg text-muted-foreground mb-8"
              >
                Find answers to common questions about SEO, our services, and how we can help your business grow
              </motion.p>

              {/* Search Bar */}
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="relative max-w-xl mx-auto"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  type="text"
                  className="pl-10 w-full rounded-lg py-3 px-4 border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="Search for questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="py-12">
          <div className="page-container">
            <div className="max-w-4xl mx-auto">
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="mb-10">
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      className="text-2xl font-bold mb-6 flex items-center"
                    >
                      <HelpCircle className="mr-2 h-6 w-6 text-primary" />
                      {category.category}
                    </motion.h2>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                    >
                      <Accordion type="single" collapsible className="w-full">
                        {category.faqs.map((faq, faqIndex) => (
                          <AccordionItem key={faqIndex} value={`item-${categoryIndex}-${faqIndex}`}>
                            <AccordionTrigger className="text-left hover:text-primary transition-colors text-lg font-medium py-4">
                              {faq.question}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground">
                              <p className="py-2">{faq.answer}</p>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </motion.div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16">
                  <HelpCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No questions found</h3>
                  <p className="text-muted-foreground mb-6">
                    We couldn't find any FAQs matching your search. Try a different term or browse our categories.
                  </p>
                  {searchTerm && (
                    <Button 
                      onClick={() => setSearchTerm('')}
                      variant="outline"
                    >
                      Clear Search
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-muted/30 py-16">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Still have questions?
              </h2>
              <p className="text-muted-foreground mb-8">
                We're here to help. Reach out to our team for personalized answers to your SEO questions.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button asChild className="bg-primary-gradient">
                  <Link to="/contact">
                    Contact Us
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/pricing" className="flex items-center">
                    View Our Services
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </PageTransition>
  );
};

export default Faqs;
